import React, { useCallback } from 'react';

import { PasswordInput, Paper, Title, Container, Button, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { ReactComponent as KiloLogo } from 'assets/svg/long_kilo_blue_white.svg';
import useResetPasswordConfirm from 'hooks/auth/useResetPasswordConfirm';
import { useNavigate, useParams } from 'react-router-dom';
import { rePasswordValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';

interface FormValues {
  password: string;
  rePassword: string;
  surname?: string;
}

const PasswordResetConfirm = (): JSX.Element => {
  const { uid, token } = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();

  const resetPasswordConfirmMutation = useResetPasswordConfirm();

  const { successNotification, errorNotification } = notificationHandler();

  const form = useForm<FormValues>({
    initialValues: {
      password: '',
      rePassword: '',
      surname: '',
    },

    validate: {
      password: value => (value ? null : 'Required'),
      rePassword: (value, values) => rePasswordValidator(values.password)(value),
    },
  });

  const handleSubmit = useCallback(
    (formData: FormValues): void => {
      if (uid && token) {
        const request = { uid, token, ...formData };
        resetPasswordConfirmMutation.mutate(request, {
          onError: error => {
            errorNotification('Password reset failed', error.response?.data.message);
          },
          onSuccess: () => {
            successNotification('Password reset successful', 'You can now use your new password.');
            navigate('/login');
          },
        });
      }
    },
    [uid, token, resetPasswordConfirmMutation, errorNotification, successNotification, navigate],
  );

  return (
    <Container size='xs' py='xl'>
      <KiloLogo width='100%' height={60} />
      <Title align='center' mt='xl' sx={{ fontWeight: 900 }}>
        Set New Password
      </Title>

      <Paper withBorder shadow='md' p={30} mt={30} radius='md'>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <PasswordInput
            label='Password'
            placeholder='•••••••'
            required
            mt='md'
            readOnly={resetPasswordConfirmMutation.isLoading}
            {...form.getInputProps('password')}
          />
          <PasswordInput
            label='Re-password'
            placeholder='•••••••'
            required
            mt='md'
            readOnly={resetPasswordConfirmMutation.isLoading}
            {...form.getInputProps('rePassword')}
          />
          <TextInput mt='md' type='hidden' {...form.getInputProps('surname')} />
          <Button fullWidth mt='xl' type='submit' loading={resetPasswordConfirmMutation.isLoading}>
            Reset
          </Button>
        </form>
      </Paper>
    </Container>
  );
};

export default PasswordResetConfirm;
